import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRequest, editRequest } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, Users, Coffee, Cloud } from 'lucide-react';
import { toast } from 'sonner';
import { PageSkeleton, Bone, BoneCard } from '../components/PageSkeleton';

export default function EditRequestPage() {
  const navigate = useNavigate();
  const { requestId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);

  // Editable fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [budgetAmount, setBudgetAmount] = useState('');
  const [roleSubtype, setRoleSubtype] = useState('');

  // Role & Execution
  const [execSetsCount, setExecSetsCount] = useState(2);
  const [execSetLength, setExecSetLength] = useState('45');
  const [execBreakLength, setExecBreakLength] = useState('15');
  const [execPerformanceMode, setExecPerformanceMode] = useState('');
  const [execRoleType, setExecRoleType] = useState('');
  const [execBillPosition, setExecBillPosition] = useState('');
  const [execMaterialSource, setExecMaterialSource] = useState('');
  const [execAnnouncementLanguage, setExecAnnouncementLanguage] = useState('');
  const [execNotes, setExecNotes] = useState('');
  const [execErrors, setExecErrors] = useState({});

  // Hospitality
  const [hospFood, setHospFood] = useState('');
  const [hospFoodDetail, setHospFoodDetail] = useState('');
  const [hospDressingRoom, setHospDressingRoom] = useState('');
  const [hospSecureStorage, setHospSecureStorage] = useState('');
  const [hospWifi, setHospWifi] = useState('');
  const [hospDrinks, setHospDrinks] = useState('');
  const [hospShower, setHospShower] = useState('');
  const [hospGreenRoom, setHospGreenRoom] = useState('');
  const [hospErrors, setHospErrors] = useState({});
  // Catering — optional; empty `provided` means the request carries no catering at all
  const [cateringProvided, setCateringProvided] = useState('');
  const [cateringServedAt, setCateringServedAt] = useState('');
  const [cateringVegetarian, setCateringVegetarian] = useState('');

  // Event Environment
  const [envSetting, setEnvSetting] = useState('');
  const [envCoveredStage, setEnvCoveredStage] = useState('');
  const [envErrors, setEnvErrors] = useState({});

  useEffect(() => {
    loadRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const loadRequest = async () => {
    try {
      const response = await getRequest(requestId);
      const req = response.data;
      setRequest(req);
      
      // Populate form fields
      setTitle(req.title || '');
      setDescription(req.description || '');
      setEventDate(req.eventDate || '');
      setStartTime(req.startTime || '');
      setEndTime(req.endTime || '');
      setBudgetAmount(req.budgetAmount?.toString() || '');
      setRoleSubtype(req.roleSubtype || '');

      // Prefill Role & Execution
      const rx = req.roleExecution || {};
      setExecSetsCount(rx.setsCount || 2);
      setExecSetLength(rx.setLengthMinutes != null ? String(rx.setLengthMinutes) : '45');
      setExecBreakLength(rx.breakLengthMinutes != null ? String(rx.breakLengthMinutes) : '15');
      setExecPerformanceMode(rx.performanceMode || '');
      setExecRoleType(rx.roleType || '');
      setExecBillPosition(rx.billPosition || '');
      setExecMaterialSource(rx.materialSource || '');
      setExecAnnouncementLanguage(rx.announcementLanguage || '');
      setExecNotes(rx.notes || '');

      // Prefill Hospitality
      const h = req.hospitality || {};
      setHospFood(h.food || '');
      setHospFoodDetail(h.foodDetail || '');
      setHospDressingRoom(h.dressingRoom || '');
      setHospSecureStorage(h.secureStorage || '');
      setHospWifi(h.wifi || '');
      setHospDrinks(h.drinks || '');
      setHospShower(h.showerAccess || '');
      setHospGreenRoom(h.greenRoom || '');

      // Prefill Catering (absent on requests created before the field existed)
      const c = req.catering || {};
      setCateringProvided(c.provided || '');
      setCateringServedAt(c.served_at || '');
      setCateringVegetarian(c.vegetarian || '');

      // Prefill Event Environment
      const ev = req.environment || {};
      setEnvSetting(ev.setting || '');
      setEnvCoveredStage(ev.coveredStage === true ? 'true' : ev.coveredStage === false ? 'false' : '');
    } catch (error) {
      toast.error('Failed to load request');
      navigate('/venues/requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title) {
      toast.error('Title is required');
      return;
    }

    // Role & Execution validation
    const rxErrors = {};
    if (!execSetsCount || execSetsCount < 1 || execSetsCount > 4) rxErrors.setsCount = 'Pick 1–4';
    const setLenNum = parseInt(execSetLength);
    if (!execSetLength || isNaN(setLenNum) || setLenNum < 15 || setLenNum > 180) rxErrors.setLength = 'Enter 15–180 minutes';
    if (execSetsCount > 1) {
      const brkNum = parseInt(execBreakLength);
      if (execBreakLength === '' || isNaN(brkNum) || brkNum < 0 || brkNum > 60) rxErrors.breakLength = 'Enter 0–60 minutes';
    }
    if (!execPerformanceMode) rxErrors.performanceMode = 'Required';
    if (!execRoleType) rxErrors.roleType = 'Required';
    if (!execBillPosition) rxErrors.billPosition = 'Required';
    if (!execMaterialSource) rxErrors.materialSource = 'Required';
    if (execNotes && execNotes.length > 280) rxErrors.notes = 'Max 280 characters';
    if (Object.keys(rxErrors).length) {
      setExecErrors(rxErrors);
      toast.error('Please complete the Role & Execution section');
      return;
    }
    setExecErrors({});

    // Hospitality validation
    const hErrors = {};
    if (!hospFood) hErrors.food = 'Required';
    if (!hospDressingRoom) hErrors.dressingRoom = 'Required';
    if (!hospSecureStorage) hErrors.secureStorage = 'Required';
    if (!hospWifi) hErrors.wifi = 'Required';
    if (!hospDrinks) hErrors.drinks = 'Required';
    if (!hospShower) hErrors.showerAccess = 'Required';
    if (!hospGreenRoom) hErrors.greenRoom = 'Required';
    if (hospFoodDetail && hospFoodDetail.length > 100) hErrors.foodDetail = 'Max 100 characters';
    if (Object.keys(hErrors).length) {
      setHospErrors(hErrors);
      toast.error('Please complete the Hospitality section');
      return;
    }
    setHospErrors({});

    // Event Environment validation
    const eErrors = {};
    if (!envSetting) eErrors.setting = 'Required';
    const needsCovered = envSetting === 'outdoor' || envSetting === 'mixed';
    if (needsCovered && envCoveredStage === '') eErrors.coveredStage = 'Required for outdoor/mixed';
    if (Object.keys(eErrors).length) {
      setEnvErrors(eErrors);
      toast.error('Please complete the Event Environment section');
      return;
    }
    setEnvErrors({});

    setSubmitting(true);
    try {
      await editRequest(requestId, {
        title,
        description,
        eventDate,
        startTime,
        endTime,
        budgetAmount: budgetAmount ? parseFloat(budgetAmount) : null,
        roleSubtype,
        roleExecution: {
          setsCount: execSetsCount,
          setLengthMinutes: parseInt(execSetLength),
          breakLengthMinutes: execSetsCount > 1 ? parseInt(execBreakLength) : null,
          performanceMode: execPerformanceMode,
          roleType: execRoleType,
          billPosition: execBillPosition,
          materialSource: execMaterialSource,
          announcementLanguage: execAnnouncementLanguage || null,
          notes: execNotes.trim() ? execNotes.trim() : null,
        },
        hospitality: {
          food: hospFood,
          foodDetail: hospFoodDetail.trim() ? hospFoodDetail.trim() : null,
          dressingRoom: hospDressingRoom,
          secureStorage: hospSecureStorage,
          wifi: hospWifi,
          drinks: hospDrinks,
          showerAccess: hospShower,
          greenRoom: hospGreenRoom,
        },
        catering: cateringProvided
          ? {
              provided: cateringProvided,
              served_at: cateringProvided !== 'none' && cateringServedAt ? cateringServedAt : null,
              vegetarian: cateringProvided !== 'none' && cateringVegetarian
                ? cateringVegetarian
                : null,
            }
          : null,
        environment: {
          setting: envSetting,
          coveredStage: (envSetting === 'outdoor' || envSetting === 'mixed')
            ? (envCoveredStage === 'true')
            : null,
        },
      });
      
      toast.success('Request updated!');
      navigate('/venues/requests');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageSkeleton label="Loading request" className="pb-24">
        <div className="flex items-start gap-4">
          <Bone w={20} h={20} className="mt-1" />
          <div className="flex-1"><Bone w={170} h={26} tone="card" /><Bone w={220} h={14} className="mt-2" /></div>
        </div>
        <div className="space-y-6 mt-6">
          {[1, 2].map((i) => (
            <BoneCard key={i} className="p-6">
              <Bone w={110} h={20} />
              {[0, 1, 2].map((f) => (
                <div key={f} className="mt-5">
                  <Bone w={70} h={12} />
                  <Bone h={f === 1 ? 76 : 36} r="md" className="mt-2" />
                </div>
              ))}
            </BoneCard>
          ))}
        </div>
      </PageSkeleton>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Request not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/venues/requests')}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Edit Request</h1>
          <p className="text-sm text-muted-foreground">Update your booking request details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Basic Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-foreground">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Jazz Vocalist for Saturday Night"
                className="bg-background border-input"
                data-testid="edit-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Additional details about the gig..."
                className="bg-background border-input min-h-[80px]"
                data-testid="edit-description"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="roleSubtype" className="text-foreground">Specific Role</Label>
              <Input
                id="roleSubtype"
                value={roleSubtype}
                onChange={(e) => setRoleSubtype(e.target.value)}
                placeholder="e.g., FOH Engineer, Lead Guitarist"
                className="bg-background border-input"
                data-testid="edit-role-subtype"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Date & Time</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="eventDate" className="text-foreground">Event Date</Label>
              <Input
                id="eventDate"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="bg-background border-input"
                data-testid="edit-event-date"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime" className="text-foreground">Start Time</Label>
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="bg-background border-input"
                  data-testid="edit-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTime" className="text-foreground">End Time</Label>
                <Input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="bg-background border-input"
                  data-testid="edit-end-time"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Budget</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="budgetAmount" className="text-foreground">Amount (€)</Label>
              <Input
                id="budgetAmount"
                type="number"
                value={budgetAmount}
                onChange={(e) => setBudgetAmount(e.target.value)}
                placeholder="e.g., 250"
                className="bg-background border-input"
                data-testid="edit-budget"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="role-execution-section">
          <CardHeader>
            <CardTitle className="text-lg text-foreground flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              Role & Execution
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Sets chips */}
            <div className="space-y-2">
              <Label className="text-foreground">How many sets? *</Label>
              <div className="flex gap-2" data-testid="exec-sets-chips">
                {[1, 2, 3, 4].map((n) => {
                  const active = execSetsCount === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        setExecSetsCount(n);
                        if (n === 1) setExecBreakLength('');
                        else if (!execBreakLength) setExecBreakLength('15');
                      }}
                      className="h-11 w-14 rounded-lg text-sm font-semibold transition-colors"
                      style={{
                        border: `1px solid ${active ? 'var(--s8-amber)' : 'var(--s8-b1-solid)'}`,
                        background: active ? 'rgba(200,146,62,0.12)' : 'var(--bg-card-inner)',
                        color: active ? 'var(--s8-amber)' : 'var(--t1)',
                        fontFamily: 'var(--s8-font-mono)',
                      }}
                      data-testid={`exec-sets-${n}`}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
              {execErrors.setsCount && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.setsCount}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exec-set-length" className="text-foreground">Set Length (minutes) *</Label>
                <Input
                  id="exec-set-length"
                  type="number"
                  value={execSetLength}
                  onChange={(e) => setExecSetLength(e.target.value)}
                  placeholder="45"
                  className="bg-background border-input"
                  min="15"
                  max="180"
                  data-testid="exec-set-length-input"
                />
                {execErrors.setLength && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.setLength}</p>}
              </div>
              {execSetsCount > 1 && (
                <div className="space-y-2">
                  <Label htmlFor="exec-break-length" className="text-foreground">Break (minutes) *</Label>
                  <Input
                    id="exec-break-length"
                    type="number"
                    value={execBreakLength}
                    onChange={(e) => setExecBreakLength(e.target.value)}
                    placeholder="15"
                    className="bg-background border-input"
                    min="0"
                    max="60"
                    data-testid="exec-break-length-input"
                  />
                  {execErrors.breakLength && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.breakLength}</p>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Performance Mode *</Label>
                <Select value={execPerformanceMode} onValueChange={setExecPerformanceMode}>
                  <SelectTrigger className="bg-background border-input" data-testid="exec-performance-mode-select">
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solo">Solo</SelectItem>
                    <SelectItem value="duo">Duo</SelectItem>
                    <SelectItem value="trio">Trio</SelectItem>
                    <SelectItem value="quartet">Quartet</SelectItem>
                    <SelectItem value="band">Band</SelectItem>
                    <SelectItem value="backing_band_provided">Backing band provided</SelectItem>
                  </SelectContent>
                </Select>
                {execErrors.performanceMode && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.performanceMode}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Role Type *</Label>
                <Select value={execRoleType} onValueChange={setExecRoleType}>
                  <SelectTrigger className="bg-background border-input" data-testid="exec-role-type-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="support">Support</SelectItem>
                    <SelectItem value="accompaniment">Accompaniment</SelectItem>
                    <SelectItem value="house_band">House band</SelectItem>
                  </SelectContent>
                </Select>
                {execErrors.roleType && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.roleType}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Bill Position *</Label>
              <Select value={execBillPosition} onValueChange={setExecBillPosition}>
                <SelectTrigger className="bg-background border-input" data-testid="exec-bill-position-select">
                  <SelectValue placeholder="Select bill position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="solo_bill">Solo bill</SelectItem>
                  <SelectItem value="headliner">Headliner</SelectItem>
                  <SelectItem value="direct_support">Direct support</SelectItem>
                  <SelectItem value="opener">Opener</SelectItem>
                  <SelectItem value="multi_act">Multi-act bill</SelectItem>
                </SelectContent>
              </Select>
              {execErrors.billPosition && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.billPosition}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Material Source *</Label>
                <Select value={execMaterialSource} onValueChange={setExecMaterialSource}>
                  <SelectTrigger className="bg-background border-input" data-testid="exec-material-source-select">
                    <SelectValue placeholder="Select material" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="own_setlist">Own setlist</SelectItem>
                    <SelectItem value="provided_setlist">Provided setlist</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                    <SelectItem value="improvised">Improvised</SelectItem>
                  </SelectContent>
                </Select>
                {execErrors.materialSource && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.materialSource}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Announcement Language</Label>
                <Select value={execAnnouncementLanguage || 'unset'} onValueChange={(v) => setExecAnnouncementLanguage(v === 'unset' ? '' : v)}>
                  <SelectTrigger className="bg-background border-input" data-testid="exec-announcement-language-select">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">—</SelectItem>
                    <SelectItem value="dutch">Dutch</SelectItem>
                    <SelectItem value="english">English</SelectItem>
                    <SelectItem value="both">Dutch + English</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="exec-notes" className="text-foreground">Notes <span className="text-muted-foreground font-normal">(optional, 280 char max)</span></Label>
              <Textarea
                id="exec-notes"
                value={execNotes}
                onChange={(e) => setExecNotes(e.target.value.slice(0, 280))}
                placeholder='e.g. "Must lead Happy Birthday sing-along at 20:00"'
                className="bg-background border-input min-h-[80px]"
                data-testid="exec-notes-input"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                {execErrors.notes ? <span style={{ color: 'var(--s8-red-flag)' }}>{execErrors.notes}</span> : <span />}
                <span>{execNotes.length}/280</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hospitality Section */}
        <Card className="bg-card border-border" data-testid="hospitality-section">
          <CardHeader>
            <CardTitle className="text-lg text-foreground flex items-center gap-2">
              <Coffee className="h-5 w-5 text-muted-foreground" />
              Hospitality
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Food *</Label>
                <Select value={hospFood} onValueChange={setHospFood}>
                  <SelectTrigger className="bg-background border-input" data-testid="hosp-food-select"><SelectValue placeholder="Select food option" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_provided">Not provided</SelectItem>
                    <SelectItem value="snacks">Snacks</SelectItem>
                    <SelectItem value="meal_provided">Meal provided</SelectItem>
                    <SelectItem value="meal_voucher">Meal voucher</SelectItem>
                    <SelectItem value="rider_specific">Per rider</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.food && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.food}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="hosp-food-detail" className="text-foreground">Food Detail <span className="text-muted-foreground font-normal">(optional, 100 char max)</span></Label>
                <Input
                  id="hosp-food-detail"
                  value={hospFoodDetail}
                  onChange={(e) => setHospFoodDetail(e.target.value.slice(0, 100))}
                  placeholder='e.g. "Vegetarian buffet at 19:30"'
                  className="bg-background border-input"
                  data-testid="hosp-food-detail-input"
                />
                {hospErrors.foodDetail && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.foodDetail}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Catering <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Select
                  value={cateringProvided}
                  onValueChange={(v) => {
                    setCateringProvided(v);
                    if (v === 'none') { setCateringServedAt(''); setCateringVegetarian(''); }
                  }}
                >
                  <SelectTrigger className="bg-background border-input" data-testid="catering-provided-select"><SelectValue placeholder="Not specified" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="at_venue">At venue</SelectItem>
                    <SelectItem value="on_request">On request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="catering-served-at" className="text-foreground">Served At</Label>
                <Input
                  id="catering-served-at"
                  type="time"
                  value={cateringServedAt}
                  onChange={(e) => setCateringServedAt(e.target.value)}
                  disabled={!cateringProvided || cateringProvided === 'none'}
                  className="bg-background border-input"
                  data-testid="catering-served-at-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Vegetarian</Label>
                <Select
                  value={cateringVegetarian}
                  onValueChange={setCateringVegetarian}
                  disabled={!cateringProvided || cateringProvided === 'none'}
                >
                  <SelectTrigger className="bg-background border-input" data-testid="catering-vegetarian-select"><SelectValue placeholder="Not specified" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="unavailable">Unavailable</SelectItem>
                    <SelectItem value="ask">Ask ahead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Dressing Room *</Label>
                <Select value={hospDressingRoom} onValueChange={setHospDressingRoom}>
                  <SelectTrigger className="bg-background border-input" data-testid="hosp-dressing-room-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="shared">Shared</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.dressingRoom && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.dressingRoom}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Secure Storage *</Label>
                <Select value={hospSecureStorage} onValueChange={setHospSecureStorage}>
                  <SelectTrigger className="bg-background border-input" data-testid="hosp-secure-storage-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="lockable_room">Lockable room</SelectItem>
                    <SelectItem value="safe">Safe</SelectItem>
                    <SelectItem value="venue_supervised">Venue supervised</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.secureStorage && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.secureStorage}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">WiFi *</Label>
                <Select value={hospWifi} onValueChange={setHospWifi}>
                  <SelectTrigger className="bg-background border-input" data-testid="hosp-wifi-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="guest_network">Guest network</SelectItem>
                    <SelectItem value="staff_network">Staff network</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.wifi && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.wifi}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Drinks *</Label>
                <Select value={hospDrinks} onValueChange={setHospDrinks}>
                  <SelectTrigger className="bg-background border-input" data-testid="hosp-drinks-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="water_only">Water only</SelectItem>
                    <SelectItem value="soft_drinks">Soft drinks</SelectItem>
                    <SelectItem value="rider_specific">Per rider</SelectItem>
                    <SelectItem value="open_bar">Open bar</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.drinks && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.drinks}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Shower Access *</Label>
                <Select value={hospShower} onValueChange={setHospShower}>
                  <SelectTrigger className="bg-background border-input" data-testid="hosp-shower-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.showerAccess && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.showerAccess}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Green Room *</Label>
                <Select value={hospGreenRoom} onValueChange={setHospGreenRoom}>
                  <SelectTrigger className="bg-background border-input" data-testid="hosp-green-room-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="shared">Shared</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.greenRoom && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.greenRoom}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Event Environment Section */}
        <Card className="bg-card border-border" data-testid="environment-section">
          <CardHeader>
            <CardTitle className="text-lg text-foreground flex items-center gap-2">
              <Cloud className="h-5 w-5 text-muted-foreground" />
              Event Environment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Setting *</Label>
                <Select value={envSetting} onValueChange={(v) => { setEnvSetting(v); if (v === 'indoor' || v === 'covered_outdoor') setEnvCoveredStage(''); }}>
                  <SelectTrigger className="bg-background border-input" data-testid="env-setting-select"><SelectValue placeholder="Select setting" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="indoor">Indoor</SelectItem>
                    <SelectItem value="outdoor">Outdoor</SelectItem>
                    <SelectItem value="covered_outdoor">Covered outdoor</SelectItem>
                    <SelectItem value="mixed">Mixed (indoor + outdoor)</SelectItem>
                  </SelectContent>
                </Select>
                {envErrors.setting && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{envErrors.setting}</p>}
              </div>
              {(envSetting === 'outdoor' || envSetting === 'mixed') && (
                <div className="space-y-2">
                  <Label className="text-foreground">Covered Stage *</Label>
                  <Select value={envCoveredStage} onValueChange={setEnvCoveredStage}>
                    <SelectTrigger className="bg-background border-input" data-testid="env-covered-stage-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                  {envErrors.coveredStage && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{envErrors.coveredStage}</p>}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-12 text-lg bg-primary hover:bg-primary/90"
          data-testid="save-request-btn"
        >
          <Save className="h-5 w-5 mr-2" />
          {submitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </form>
    </div>
  );
}
